import { Build, Delete, Description, Folder, People, Public, Save, Search, Star, Widgets } from '@material-ui/icons'
import { useLogger, useRepository } from '@sensenet/hooks-react'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import { PATHS, resolvePathParams } from '../application-paths'
import { Icon } from '../components/Icon'
import { ResponsivePersonalSettings } from '../context'
import { FAVORITES_ROOT_PARENT_PATH } from '../services/favorites-constants'
import { CustomContentDrawerItem, DrawerItem as DrawerItemSetting } from '../services/PersonalSettings'
import { useLocalization } from '.'

export interface DrawerItem {
  primaryText: string
  secondaryText: string
  url: string
  icon: JSX.Element
  root?: string
  itemType: string
  systemItem?: boolean
}

type DrawerType = CustomContentDrawerItem | DrawerItemSetting<any>

export const useDrawerItems = () => {
  const settings = useContext(ResponsivePersonalSettings)
  const localization = useLocalization().drawer
  const repo = useRepository()
  const logger = useLogger('use-drawer-items')

  const [drawerItems, setDrawerItems] = useState<DrawerItem[]>([])

  const builtInDrawerItems: Array<DrawerItemSetting<any>> = useMemo(
    () => [
      {
        itemType: 'Content',
        settings: { root: PATHS.content.snPath },
        permissions: [
          {
            path: PATHS.content.snPath,
            action: 'Browse',
          },
        ],
        systemItem: false,
      },
      {
        itemType: 'Favorites',
        settings: { root: PATHS.favorites.snPath },
        permissions: [
          {
            path: FAVORITES_ROOT_PARENT_PATH,
            action: 'Browse',
          },
        ],
        systemItem: false,
      },
      {
        itemType: 'UsersAndGroups',
        settings: { root: PATHS.usersAndGroups.snPath },
        permissions: [
          {
            path: PATHS.usersAndGroups.snPath,
            action: 'Browse',
          },
        ],
        systemItem: false,
      },
      {
        itemType: 'SavedQueries',
        systemItem: false,
      },
      {
        itemType: 'Trash',
        settings: { root: PATHS.trash.snPath },
        permissions: [
          {
            path: PATHS.trash.snPath,
            action: 'Edit',
          },
        ],
        systemItem: false,
      },
      {
        itemType: 'ContentTypes',
        settings: { root: PATHS.contentTypes.snPath },
        permissions: [
          {
            path: PATHS.contentTypes.snPath,
            action: 'Add',
          },
        ],
        systemItem: true,
      },
      {
        itemType: 'ContentTemplates',
        settings: { root: PATHS.contentTemplates.snPath },
        permissions: [
          {
            path: PATHS.contentTemplates.snPath,
            action: 'Browse',
          },
        ],
        systemItem: true,
      },
      {
        itemType: 'System',
        systemItem: true,
      },
    ],
    [],
  )

  useEffect(() => {
    let isMounted = true

    const getItemNameFromSettings = (item: DrawerItemSetting<any>) => {
      return item.settings?.title || localization.titles[item.itemType] || '!NO TITLE!'
    }

    const getItemDescriptionFromSettings = (item: DrawerItemSetting<any>) => {
      return item.settings?.description || localization.descriptions[item.itemType]
    }

    const getIconFromSetting = (item: DrawerType) => {
      switch (item.itemType) {
        case 'SavedQueries':
          return (
            <div>
              <Search />
              <Save className="secondary-icon" />
            </div>
          )
        case 'Content':
          return <Public />
        case 'Favorites':
          return <Star />
        case 'ContentTemplates':
          return (
            <div>
              <Public />
              <Description className="secondary-icon" />
            </div>
          )
        case 'UsersAndGroups':
          return <People />
        case 'Trash':
          return <Delete />
        case 'ContentTypes':
          return <Widgets />
        case 'CustomContent':
          return item.settings?.icon ? <Icon item={{ ContentTypeName: item.settings.icon }} /> : <Folder />
        case 'System':
          return <Build />
        // no default
      }
    }

    const getUrlFromSetting = (item: DrawerType) => {
      switch (item.itemType) {
        case 'SavedQueries':
          return resolvePathParams({ path: PATHS.savedQueries.appPath })
        case 'Content':
          return resolvePathParams({
            path: PATHS.content.appPath,
            params: { browseType: settings.content.browseType },
          })
        case 'Favorites':
          return resolvePathParams({
            path: PATHS.favorites.appPath,
            params: { browseType: settings.content.browseType },
          })
        case 'ContentTemplates':
          return resolvePathParams({
            path: PATHS.contentTemplates.appPath,
            params: { browseType: settings.content.browseType },
          })
        case 'UsersAndGroups':
          return resolvePathParams({
            path: PATHS.usersAndGroups.appPath,
            params: { browseType: settings.content.browseType },
          })
        case 'Trash':
          return resolvePathParams({
            path: PATHS.trash.appPath,
            params: { browseType: settings.content.browseType },
          })
        case 'ContentTypes':
          return resolvePathParams({
            path: PATHS.contentTypes.appPath,
            params: { browseType: settings.content.browseType },
          })
        case 'CustomContent':
          return resolvePathParams({
            path: PATHS.custom.appPath,
            params: {
              browseType: settings.content.browseType,
              path: (item as CustomContentDrawerItem).settings?.appPath || '',
            },
          })
        case 'System':
          return resolvePathParams({ path: PATHS.settings.appPath, params: { submenu: 'settings' } })
        default:
          return '/'
      }
    }

    const getItemFromSettings = (setting: DrawerItemSetting<any>) => ({
      icon: getIconFromSetting(setting),
      primaryText: getItemNameFromSettings(setting),
      secondaryText: getItemDescriptionFromSettings(setting),
      url: getUrlFromSetting(setting),
      root: setting.settings?.root,
      itemType: setting.itemType,
      systemItem: setting.systemItem,
    })

    ;(async () => {
      const filtered = await builtInDrawerItems.filterAsync(async (item) => {
        if (!item.permissions?.length) return true

        try {
          for (const permission of item.permissions) {
            const actions = await repo.getActions({ idOrPath: permission.path })
            const action = actions.d.results.find((a) => a.Name === permission.action)
            if (!action) return false
          }
        } catch (error) {
          logger.debug({
            message: error.message,
            data: { error },
          })
          return false
        }
        return true
      })
      if (isMounted) {
        setDrawerItems(filtered.map(getItemFromSettings))
      }
    })()
    return () => {
      isMounted = false
    }
  }, [
    localization.descriptions,
    localization.titles,
    logger,
    repo,
    settings.content,
    settings.content.fields,
    settings.drawer.items,
    builtInDrawerItems,
  ])

  return drawerItems
}
